import { MsalAuthProvider } from "react-aad-msal";
import * as BrowserHistory from "history";

import pkg from "../package.json";

import { ISettings } from "./interfaces";
import { Environment, getConfiguration, IConfiguration } from "./config";
import { Logger } from "./utils/Logger";
import * as IndexedDB from "./IndexedDB";
import {
	ApiCore,
	WidgetsApi,
	SendumApi,
	ApiGatewayClient,
	AuthKeyClient,
	AuthTokenClient,
	AzureApiClient,
	EnvirotainerApiClient,
	IAuthenticator,
	VoyagerApi,
	UnAuthClient,
} from "./services/Api";

export class Sendum {
	private static readonly logger = new Logger("Sendum");
	private static instance: Sendum;

	private dbInitiated: boolean;
	private settings: ISettings;
	private config: IConfiguration;
	private apiClient?: ApiCore;
	private loader: JSX.Element | null = null;
	private browserHistory: BrowserHistory.History;

	private constructor() {
		const appVersion = localStorage.getItem("SC_APP_VERSION");
		Sendum.logger.debug("Sendum >> version", appVersion, "-->", pkg.version, !appVersion || appVersion !== pkg.version ? "clean" : "");

		if (!appVersion || appVersion !== pkg.version) {
			Sendum.clean().then(() => {
				localStorage.setItem("SC_APP_VERSION", pkg.version as string);
				window.location.reload();
			}).catch(error => {
				Sendum.logger.error(error);
				throw new Error("Clean site data failed");
			});
		} else {
			this.config = getConfiguration();
			new IndexedDB.IDB()
				.init()
				.then(value => {
					if (value) {
						this.dbInitiated = true;
						void IndexedDB.clearOldData();
					} else {
						throw new Error("IndexedDB does not ready");
					}
				}).catch(() => {
					throw new Error("IndexedDB does not ready");
				});
		}
	}

	private static async clean(): Promise<void> {
		if ("caches" in window) {
			const names = await caches.keys();
			names.forEach(n => {
				void caches.delete(n);
			});
		}

		return new IndexedDB.IDB().clean();
	}

	private static getInstance(): Sendum {
		if (!Sendum.instance) {
			Sendum.instance = new Sendum();
		}

		return Sendum.instance;
	}

	public static init(settings: ISettings): void {
		const sendum = Sendum.getInstance();
		if (sendum.settings) {
			return;
		}
		sendum.settings = settings;
		sendum.config = getConfiguration(settings.env);
	}

	public static initApiGatewayClient(): void {
		const S = Sendum.getInstance();
		if (!(S.apiClient instanceof ApiGatewayClient)) {
			const config = getConfiguration();
			S.apiClient = new ApiGatewayClient(config.cognitoAuthWidgetsApiUrl, config.cognitoAuthWidgetsApiVersion, config.baseApiPath);
			WidgetsApi.apiClient = S.apiClient;
			SendumApi.apiClient = new ApiGatewayClient(config.cognitoAuthApiUrl, config.cognitoAuthApiVersion, "");
		}
	}

	public static initAuthKeyClient(apiKey?: string): void {
		const S = Sendum.getInstance();
		if (!(S.apiClient instanceof AuthKeyClient)) {
			S.apiClient = new AuthKeyClient(Sendum.getEnv(), apiKey);
			WidgetsApi.apiClient = S.apiClient;
			VoyagerApi.apiClient = new UnAuthClient();
		}
	}

	public static initAuthTokenClient(token: string): void {
		const S = Sendum.getInstance();
		if (!(S.apiClient instanceof AuthTokenClient)) {
			S.apiClient = new AuthTokenClient(token);
			WidgetsApi.apiClient = S.apiClient;
		}
	}

	public static initEnvirotainerApiClient(authenticator: IAuthenticator): void {
		const S = Sendum.getInstance();
		if (!(S.apiClient instanceof EnvirotainerApiClient)) {
			S.apiClient = new EnvirotainerApiClient(authenticator, Sendum.getEnv());
			WidgetsApi.apiClient = S.apiClient;
		}
	}

	public static initAzureApiClient(authProvide: MsalAuthProvider): void {
		const S = Sendum.getInstance();
		if (!(S.apiClient instanceof AzureApiClient)) {
			S.apiClient = new AzureApiClient(authProvide, Sendum.getEnv());
			WidgetsApi.apiClient = S.apiClient;
		}
	}

	public static getWidgetsApiClient(): ApiCore {
		const S = Sendum.getInstance();
		if (!S.apiClient) {
			throw new Error("API Client is not initiated");
		}

		return S.apiClient;
	}

	public static isDBInitiated(): boolean {
		return Sendum.getInstance().dbInitiated;
	}

	public static getSettings(): ISettings {
		return Sendum.getInstance().settings;
	}

	public static getEnv(): Environment | undefined {
		return Sendum.getInstance().settings.env || process.env.REACT_APP_SENDUM_CONFIG_ENV as Environment || Environment.DEV;
	}

	public static getConfig(): IConfiguration {
		return Sendum.getInstance().config;
	}

	public static setLoader(loader: JSX.Element): void {
		const S: Sendum = Sendum.getInstance();
		if (loader !== null) {
			S.loader = loader;
		}
	}

	public static getLoader(): JSX.Element | null {
		return Sendum.getInstance().loader;
	}

	public static setBrowserHistory(browserHistory: BrowserHistory.History): void {
		Sendum.getInstance().browserHistory = browserHistory;
	}

	public static getBrowserHistory(): BrowserHistory.History {
		return Sendum.getInstance().browserHistory;
	}
}
